import * as React from 'react';
import { FC, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ValueType } from 'react-select';
import Skeleton from 'react-loading-skeleton';

import {
  CurriculumModules,
  CurriculumSubject,
  ModuleHierarchy,
} from 'labxchange-client';
import { StandardPageLayout, showErrorMessage } from 'ui/components';
import { CurriculumsApi } from 'global/api';
import { history } from 'global/history';
import { WrappedMessage } from 'utils';

import messages from '../../displayMessages';
import OverviewPageHeader from '../OverviewPageHeader/OverviewPageHeader';
import { SelectDropdownOption } from '../../types';
import { createSubjectGradeOptions, renderModules } from '../../utils';

export const CurriculumOverviewPage: FC = () => {
  const location = useLocation();
  const pathSegments = location.pathname.split('/').filter((k) => Boolean(k));

  const [, curriculum, subject, grade] = pathSegments;

  const [loading, setLoading] = useState<boolean>(false);
  const [selectedSubject, setSelectedSubject] = useState<string>(subject || '');
  const [selectedGrade, setSelectedGrade] = useState<string>(grade || '');
  const [curriculumModules, setcurriculumModules] = useState<ModuleHierarchy[]>([]);
  const [curriculumSubjects, setCurriculumSubjects] = useState<CurriculumSubject[]>([]);
  const [curriculumName, setCurriculumName] = useState<string>('');

  useEffect(() => {
    const fetchData = async () => {
      if (curriculum && subject && grade) {
        setLoading(true);
        try {
          const response: CurriculumModules = await CurriculumsApi.read({
            id: curriculum,
            subject: selectedSubject,
            grade: selectedGrade,
          });

          setcurriculumModules(response?.modules || []);
          setCurriculumSubjects(response?.subjects || []);
          setCurriculumName(response?.name || 'Curriculum');

        } catch (error) {
          setcurriculumModules([]);
          showErrorMessage(
            <WrappedMessage message={messages.curriculumOverviewPageError} />
          );
        }
        setLoading(false);
      }
    };
    fetchData();
  }, [grade, selectedSubject]);

  const onSubjectChange = (selectedOption: ValueType<SelectDropdownOption>) => {
    if (selectedOption) {
      const selectedSubjectValue = (selectedOption as SelectDropdownOption).value;
      setSelectedSubject(selectedSubjectValue);
      history.replace(`/c/${curriculum}/${selectedSubjectValue}/${selectedGrade}`);
    }
  };

  const onGradeChange = (selectedOption: ValueType<SelectDropdownOption>) => {
    if (selectedOption) {
      const selectedGradeValue = (selectedOption as SelectDropdownOption).value;
      setSelectedGrade(selectedGradeValue);
      history.replace(`/c/${curriculum}/${selectedSubject}/${selectedGradeValue}`);
    }
  };

  const renderCurriculum = () => {
    if (loading) {
      return (
        <div className='curriculum-module-container' data-testid='loading-container'>
          <Skeleton width='40%' height={250} count={4} />
        </div>
      );
    } else {
      if (!curriculumModules.length) {
        return (
          <p className='curriculum-empty-message' data-testid='empty-msg-container'>
            <WrappedMessage message={messages.emptyCurriculumMessage} />
          </p>
        );
      } else {
        return renderModules(curriculumModules, curriculum, subject, grade, 1);
      }
    }
  };


  const { subjectOptArray: subjectOptions, gradeOptArray: gradeOptions } = createSubjectGradeOptions(curriculumSubjects, selectedSubject);
  const subjectDefaultValue = subjectOptions.find(option => option.value === selectedSubject);
  const gradeDefaultValue = gradeOptions.find(option => option.value === selectedGrade);

  return (
    <StandardPageLayout
        backgroundStyle='light-header'
        mainClassName='curriculum-overview-page'
        pageTitle={messages.curriculumPageTitle}
        pageTitleValues={{name: curriculumName}}
        headerFeature={
          <OverviewPageHeader
            loading={loading}
            onSubjectChange={onSubjectChange}
            onGradeChange={onGradeChange}
            subjectDefaultValue={subjectDefaultValue}
            gradeDefaultValue={gradeDefaultValue}
            subjectOptions={subjectOptions}
            gradeOptions={gradeOptions}
            curriculumTitle={curriculumName}
          />
        }
    >
      {renderCurriculum()}
    </StandardPageLayout>
  );
};
